import axios, {
    AxiosError, type AxiosHeaderValue,
    type AxiosInstance,
    type AxiosResponse,
    type RawAxiosRequestHeaders,
    type InternalAxiosRequestConfig
} from "axios";
import qs from 'qs';
import { type ICookie} from 'cookie-universal';
import {getCookie, removeCookie} from '@@/package/utils/CookieUtil';
import { type PublicRuntimeConfig} from "@nuxt/schema";
import { type ApplicationContextInterface} from "@@/package/application/applicationContext";
import {useRouter} from "#imports";
import {RouteName} from "~~/package/enum/RouteName";
import {useUserService} from "~/composables/service/useUserService";
import {useAuthChannel} from "~/composables/useAuthChannel";
import {useAssessmentSeat} from "~/composables/useAssessmentSeat";
import {useExamSession} from "~/composables/useExamSession";
import {useUserStore} from "~/store/user/userStore";
import { CookieKeyType } from "~~/package/mapper/common/model/CookieKeyType";
import {nextXCountry} from "~/utils/cookieConsent";

export default class HttpClient {
    protected httpInstance: AxiosInstance;

    protected cookies: ICookie | null = null;

    protected config: PublicRuntimeConfig | null = null;

    constructor(context: ApplicationContextInterface) {
        this.config = context.getConfig();
        this.cookies = context.getCookies();
        this.httpInstance = axios.create({
            baseURL: this.config.API_URL as string,
            timeout: 40000,
            headers: {'Content-Type': 'application/json'},
            withCredentials: true
        });
        this.setInterceptors();
    }

    private setInterceptors() {
        // @ts-ignore
        this.httpInstance.interceptors.request.use(
          (config: InternalAxiosRequestConfig) => {
              config.headers = config.headers || {};
              (config as any).meta = { requestHeaders: { ...config.headers } };
              return config;
          },
          (error: AxiosError) => {
              return Promise.reject(error);
          }
        );

        // @ts-ignore
        this.httpInstance.interceptors.response.use(
            (response: AxiosResponse) => {
                this.handleXCountry(response);
                return response;
            },
            (error: AxiosError) => {
                this.handleSeatLost(error);
                if (error.config && (error.config as any).meta) {
                    if((error.config as any).meta.requestHeaders["Use-Interceptor"] === "true"){
                        this.handleGlobalError(error)
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    /**
     * TSK-11215: a write rejected with 409 CP_1015 means this screen lost its seat. Flip the
     * flag immediately so the termination popup appears on the failed action instead of the
     * candidate waiting up to one poll interval to find out. Runs for every request, not just
     * Use-Interceptor ones, because the save/submit calls do not opt into the interceptor.
     */
    private handleSeatLost(error: AxiosError): void {
        if (error.response?.status !== 409) return;
        const data = (error.response.data as {data?: string} | undefined)?.data;
        if (data === 'CP_1015') {
            useAssessmentSeat().markLost();
        }
    }

    private handleGlobalError(error: AxiosError): void {
        if (error.response?.status !== 401) return;

        // #2260 (D1, D2): on the taking screen a dead session must NOT run the logout cascade —
        // router.replace(Root) is blocked by the page's route-leave guard and userLogOut() blanks
        // the uuid the S3 auto-save path is built from. Show the session-expired popup instead.
        const examSession = useExamSession();
        if (examSession.isActive()) {
            examSession.expire();
            return;
        }

        // Anonymous visitors on no-login routes (sign/auth/home/index/policy) get
        // an EXPECTED 401 from the app-load session probe (GET /v2/user). That is
        // NOT a session death and must be ignored entirely. Running the cascade
        // below — in particular useAuthChannel().postLogout() — makes the
        // authChannel plugin (9.authChannel.client.ts) replace the route with
        // RouteName.Root, so /sign bounces to home before the login form renders
        // and login becomes unreachable. So on these routes: do nothing.
        const currentName = useRouter().currentRoute.value.name;
        const topSegment = typeof currentName === 'string' ? currentName.split('-')[0] : '';
        const noLoginRoutes: RouteName[] = [
            RouteName.Sign, RouteName.Auth, RouteName.Home, RouteName.Root, RouteName.Policy,
            RouteName.ErrorCode, RouteName.Catchall,
        ];
        if (noLoginRoutes.includes(topSegment as RouteName)) return;

        // 처음부터 비로그인 상태에서 session probe가 401을 받은 경우 (= 세션 만료가 아닌 미인증 초기 접근).
        // cascade를 실행하면 userAuth의 /sign redirect를 root redirect가 덮어써서 sign으로 이동 불가.
        // uuid가 없으면 로그인한 적 없는 것이므로 cascade 건너뛰고 userAuth에 위임.
        if (!useUserStore().userV2?.uuid) return;

        // SEC-1 (TSK-10568): clear server-side HttpOnly cookie via signOut.
        // Use raw axios call (no interceptor header) to avoid 401-recursion.
        this.httpInstance.post('/user/signOut').catch(() => { /* best-effort */ });
        removeCookie(CookieKeyType.ACCESS_TOKEN); // legacy JS-cookie cleanup
        removeCookie(CookieKeyType.OPEN_CREDIT_BANNER);
        useUserService().userLogOut();
        // token-invalidation: a 401 means this session is dead — sync other tabs.
        useAuthChannel().postLogout();
        useRouter().replace({name: RouteName.Root}).then(() => {
            console.error("Unauthorized")
        });
    }

    private handleXCountry(response: AxiosResponse): void {
        const userStore = useUserStore();
        userStore.xCountry = nextXCountry(userStore.xCountry, response.headers['x-country']);
    }

    public get http(): AxiosInstance {
        // @ts-ignore
        return this.httpInstance;
    }

    public get<T>(url: any, params?: {[key: string]: any}, includeToken = false, lang : string | null = null, isFormDataType = false, useInterceptor = true, customHeader: {[key: string]: AxiosHeaderValue} = {}): Promise<T> {
        const headers: RawAxiosRequestHeaders = this.makeRequestHeaders(includeToken, isFormDataType, this.langCookie(lang), useInterceptor);
        const paramsSerializer = {
            serialize: (params: any) => { return qs.stringify(params, {arrayFormat: 'repeat'}); }
        }
        return this.http.get(url, {params, headers: {...headers, ...customHeader}, paramsSerializer});
    }

    public delete<T>(url: any, params?: {[key: string]: any}, includeToken = false, isFormDataType = false): Promise<T> {
        const headers: RawAxiosRequestHeaders = this.makeRequestHeaders(includeToken, isFormDataType);
        const paramsSerializer = {
            serialize: (params: any) => { return qs.stringify(params, {arrayFormat: 'repeat'}); }
        }
        return this.http.delete(url, {params, headers, paramsSerializer});
    }

    public post<T, R>(url: any, data: R, includeToken = false, lang : string | null = null, isFormDataType = false, customHeader: {[key: string]: AxiosHeaderValue} = {}): Promise<T> {
        const headers: RawAxiosRequestHeaders = this.makeRequestHeaders(includeToken, isFormDataType, this.langCookie(lang));
        return this.http.post(url, data, {headers: {...headers, ...customHeader}});
    }

    public patch<T, R>(url: any, data: R, includeToken = false, lang : string | null = null, isFormDataType = false): Promise<T> {
        const headers: RawAxiosRequestHeaders = this.makeRequestHeaders(includeToken, isFormDataType, this.langCookie(lang));
        return this.http.patch(url, data, {headers});
    }

    public put<T, R>(url: any, data: R, includeToken = false, isFormDataType = false, customHeader: {[key: string]: AxiosHeaderValue} = {}): Promise<T> {
        const headers: RawAxiosRequestHeaders = this.makeRequestHeaders(includeToken, isFormDataType);
        return this.http.put(url, data, {headers: {...headers, ...customHeader}});
    }

    private langCookie(lang: string | null) {
        const langData = lang ?? getCookie(CookieKeyType.LANG) ?? "EN"
        return langData.toUpperCase()
    }

    protected get applicationContentType(): string {
        return 'application/json';
    }

    protected get formDataContentType(): string {
        return 'multipart/form-data';
    }

    private makeRequestHeaders(includeToken: boolean, isFormDataType = false, lang?: string | undefined, useInterceptor?: boolean | undefined): {[key: string]: string} {
        // SEC-1 (TSK-10568): legacy JS-readable cookie is being phased out.
        // - New sessions: HttpOnly cookie sent automatically by the browser via withCredentials.
        // - Legacy sessions: still have a JS-readable cookie; attach Bearer for backwards compat
        //   until those tokens expire (≤30 days).
        const legacyToken = getCookie<string>(CookieKeyType.ACCESS_TOKEN);
        const headers: {[key: string]: string} = {
            'Content-Type': isFormDataType ? this.formDataContentType : this.applicationContentType,
            'LANG': lang as string,
            'Use-Interceptor': useInterceptor ? "true" : "false"
        };
        if (includeToken && legacyToken) {
            headers.authorization = `Bearer ${legacyToken}`;
        }
        return headers;
    }
}
